import {findObjectUseCase} from "../../../../usecases/object";
import { buildDateRangeConditions, buildHolidayWhere, buildSuspensionWhere } from "../../helper/queryHelper";
import { _toDate, _toISOString } from "../../helper/dateHelper";
import { getWorkingDaysInRange } from "./utils/ScheduleUtils";

const orgMatch = (employee, doc) => {
  if (doc?.selectAll === true) return true;
  const check = (key, empKey) => {
    const arr = Array.isArray(doc?.[key]) ? doc[key] : [];
    return arr.length > 0 && employee?.[empKey] != null && arr.includes(employee[empKey]);
  };
  return (
    check("plantilla", "cluster") ||
    check("units", "unit") ||
    check("sections", "section") ||
    check("divisions", "division") ||
    check("offices", "office") ||
    check("regions", "region")
  );
};

const makeRangeBound = (r) => {
  const startRaw = r?.parsed?.startDate ?? r?.startDate;
  const endRaw = r?.parsed?.endDate ?? r?.endDate;
  if (!startRaw || !endRaw) return null;
  const startMs = _toDate(new Date(startRaw), true).getTime();
  const endMs = _toDate(new Date(endRaw), true).getTime();
  return { startMs, endMs };
};

export const fetchLookupData = async (employees, parsedDateRanges, isArrayDate = false, airportSched) => {
    const employeeIds = employees.map((e) => e?.id);

    const { holidayDateConditions, suspensionDateConditions, manualDtrDateConditions, leaveDateConditions, obDateConditions, timeoffDateConditions } = buildDateRangeConditions(parsedDateRanges);

    console.log("manualDtrDateConditions", manualDtrDateConditions);
    
    const orgOrMap = new Map();
    const addOrg = (key, value) => {
      if (value != null) {
        const mapKey = `${key}:${value}`;
        if (!orgOrMap.has(mapKey)) {
          orgOrMap.set(mapKey, { [key]: value });
        }
      }
    };
    
    employees.forEach((e) => {
      if (!e) return;
      addOrg("regions", e.region);
      addOrg("offices", e.office);
      addOrg("divisions", e.division);
      addOrg("sections", e.section);
      addOrg("units", e.unit);
      addOrg("plantilla", e.cluster);
    });
    orgOrMap.set("selectAll:true", { selectAll: true });
    const orgOrSelectAll = Array.from(orgOrMap.values());

    const BATCH_SIZE = 100;
    const idBatches = [];
    for (let i = 0; i < employeeIds.length; i += BATCH_SIZE) {
      idBatches.push(employeeIds.slice(i, i + BATCH_SIZE));
    }

    const orgKeys = [
      "type",
      "selectAll",
      "regions",
      "offices",
      "divisions",
      "sections",
      "units",
      "plantilla",
    ];

    const [holidays, suspension] = await Promise.all([
      findObjectUseCase().execute("holidays", {
        where: buildHolidayWhere(holidayDateConditions, orgOrSelectAll),
        keys: [...orgKeys, "date"],
      }),
      findObjectUseCase().execute("suspension", {
        where: buildSuspensionWhere(suspensionDateConditions, orgOrSelectAll),
        keys: [...orgKeys, "dateFrom", "dateTo"],
      }),
    ]);


    const fetchBatched = (collection, whereFn, keys) =>
      Promise.all(
        idBatches.map((batch) => {
          const query = {
            where: whereFn(batch),
          };
          if (keys !== undefined) {
            query.keys = keys;
          }
          return findObjectUseCase().execute(collection, query);
        })
      ).then((results) => results.flat());

    const [leave_requests, manual_dtrs, ob_management, timeoff] = await Promise.all([
      fetchBatched("leave_request", (batch) => ({
        stats: "Approved",
        // categoryType: "With Pay",
        "employee.id": { $in: batch },
        ...(leaveDateConditions.length > 0 ? { $or: leaveDateConditions } : {}),
      }), [
        "selectedType",
        "dateRange",
        "employee",
        "startdate",
        "enddate",
        "withoutPayDateRange",
        "categoryType"
      ]),
      fetchBatched("manual_dtr", (batch) => ({
        "employees.id": { $in: batch },
        ...(manualDtrDateConditions.length > 0 ? { $or: manualDtrDateConditions } : {}),
      }), ["categoryType", "employees", "dateRange"]),
      fetchBatched("ob_management", (batch) => ({
        "employee.id": { $in: batch },
        status: "Approved",
        ...(obDateConditions.length > 0 ? { $or: obDateConditions } : {}),
      }), ["selectedDate", "employee", "status", "obDates"]),
      fetchBatched("timeoff", (batch) => ({
        "employee.id": { $in: batch },
        ...(timeoffDateConditions.length > 0 ? { $or: timeoffDateConditions } : {}),
      }), ["employee", "date"]),
    ]);

    const targets = new Set([
      "Forced/Mandatory Leave",
      "Special Leave Privilege",
    ]);

    const result = {};

    const workingDaysByEmployee = await getWorkingDaysInRange(
      employeeIds,
      parsedDateRanges,
      isArrayDate,
    );

    for (let i = 0; i < employees.length; i++) {
      const employee = employees[i];
      const empId = employee?.id;
      const isTwentyFourSeven = ["12", "9"].some((prefix) =>
        employee?.twentyFourSeven?.startsWith(prefix)
      );

      const workingDaysForEmp = workingDaysByEmployee.get(empId) || {};
      const workingDaysSetArray = workingDaysForEmp.workingDaysSetArray || [];
      const workingDaysCount = workingDaysForEmp.workingDaysCount ?? (isArrayDate ? [] : 0);

      const workingDaysSet = new Set();
      for (const set of workingDaysSetArray) {
        for (const date of set) {
          workingDaysSet.add(date);
        }
      }

      if (!empId) continue;

      const holidaySet = new Set();
      for (const h of holidays) {
        if (orgMatch(employee, h)) {
          const holidayDate = new Date(h.date);
          const holidayISO = _toISOString(holidayDate);
          holidaySet.add(holidayISO);
        }
      }


      const suspensionSet = new Set();
      const suspensionRangesByDate = new Map(); // Map<dateString, Array<{startMin, endMin}>>
      for (const s of suspension) {
        if (orgMatch(employee, s)) {
          const from = new Date(s.dateFrom);
          const to = new Date(s.dateTo);
          
          const suspensionStartMin = from.getHours() * 60 + from.getMinutes();
          const suspensionEndMin = to.getHours() * 60 + to.getMinutes();
          
          const fromDateStr = _toISOString(from);
          const toDateStr = _toISOString(to);

          const toDateOnly = _toDate(to, true);
          
          let cur = _toDate(from, true);;
          while (cur <= toDateOnly) {
            const curDateStr = _toISOString(cur);
            suspensionSet.add(curDateStr);
            
            let startMin = suspensionStartMin;
            let endMin = suspensionEndMin;
            
            if (fromDateStr !== toDateStr) {
              if (curDateStr === fromDateStr) {
                // First day: from start time to end of day (1440 minutes)
                endMin = 1440;
              } else if (curDateStr === toDateStr) {
                // Last day: from start of day (0) to end time
                startMin = 0;
              } else {
                // Middle days: full day (0 to 1440)
                startMin = 0;
                endMin = 1440;
              }
            }
            
            if (!suspensionRangesByDate.has(curDateStr)) {
              suspensionRangesByDate.set(curDateStr, []);
            }
            suspensionRangesByDate.get(curDateStr).push({ startMin, endMin });
            
            cur.setDate(cur.getDate() + 1);
          }
        }
      }

      const manualDtrSet = new Set();
      for (const item of manual_dtrs) {
        const hasEmployee = item?.employees.length
          ? item.employees.some((e) => e?.id === empId)
          : false;

        if (hasEmployee) {
          for (const rangeArray of item?.dateRange || []) {
            if (!Array.isArray(rangeArray)) continue;
            for (const [start, end] of rangeArray) {
              if (!start || !end) continue;
              let cur = new Date(start);
              const endDate = new Date(end);
              while (cur <= endDate) {
                manualDtrSet.add(_toISOString(cur));
                cur.setDate(cur.getDate() + 1);
              }
            }
          }
        }
      }

      const obSet = new Set();
      for (const ob of ob_management) {
        if (ob?.employee?.id === empId) {
          const obDates = ob?.obDates || [];
          for (const dateStr of obDates) {
            if (dateStr) {
              const obDate = new Date(dateStr);
              obSet.add(_toISOString(obDate));
            }
          }
        }
      }

      const timeoffSet = new Set();
      for (const to of timeoff) {
        if (to?.employee?.id === empId) {
          const timeoffDate = to?.date;
          if (timeoffDate) {
            const date = new Date(timeoffDate);
            timeoffSet.add(_toISOString(date));
          }
        }
      }

      const leaveWithoutPaySet = new Set();
      const leaveWithPaySet = new Set();
      const mandatoryLeaveSet = new Set();

        for (const leave of leave_requests) {
        if (leave.employee?.id === empId) {
          const mandatoryLeave = targets.has((leave?.selectedType || "").trim());
          const categoryType = (leave?.categoryType || "").trim();

          // 1) Explicit withoutPayDateRange (retain existing behavior)
          let hasWithoutPayRanges = false;
          if (Array.isArray(leave?.withoutPayDateRange)) {
            for (const rangeArray of leave.withoutPayDateRange) {
              if (!Array.isArray(rangeArray)) continue;
              for (const [start, end] of rangeArray) {
                if (!start || !end) continue;
                hasWithoutPayRanges = true;
                let cur = new Date(start);
                const endDate = new Date(end);
                while (cur <= endDate) {
                  leaveWithoutPaySet.add(_toISOString(cur));
                  cur.setDate(cur.getDate() + 1);
                }
              }
            }
          }

          if (categoryType === "Without Pay" && !hasWithoutPayRanges) {
            let cur = new Date(leave.startdate);
            const leavesEnd = new Date(leave.enddate);
            while (cur <= leavesEnd) {
              leaveWithoutPaySet.add(_toISOString(cur));
              cur.setDate(cur.getDate() + 1);
            }
          }

          if (categoryType === "With Pay") {
            let cur = new Date(leave.startdate);
            const leavesEnd = new Date(leave.enddate);
            while (cur <= leavesEnd) {
              const dateStr = _toISOString(cur);
              if (!leaveWithoutPaySet.has(dateStr)) {
                leaveWithPaySet.add(dateStr);
                if (mandatoryLeave) {
                  mandatoryLeaveSet.add(dateStr);
                }
              }
              cur.setDate(cur.getDate() + 1);
            }
          }
        }
      }


      const leaveWithoutPaySetArray =
        isArrayDate && Array.isArray(parsedDateRanges)
          ? parsedDateRanges.map(() => new Set())
          : [];

      if (isArrayDate && Array.isArray(parsedDateRanges) && leaveWithoutPaySet.size) {
        const rangeBounds = parsedDateRanges.map(makeRangeBound);

        for (const iso of leaveWithoutPaySet) {
          const ms = _toDate(new Date(iso), true).getTime();
          rangeBounds.forEach((b, idx) => {
            if (!b) return;
            if (ms >= b.startMs && ms <= b.endMs) leaveWithoutPaySetArray[idx].add(iso);
          });
        }
      }

      const airportDates = new Set();
      for (const sched of airportSched) {
        const start = new Date(sched.startDate);
        const end = new Date(sched.endDate);
        for (const relation of sched.assignRelation || []) {
          if (relation.employee?.id === empId) {
            let d = new Date(start);
            while (d <= end) {
              airportDates.add(_toISOString(d));
              d.setDate(d.getDate() + 1);
            }
          }
        }
      }

      const userID = employee?.biometricId ?? employee?.id;
      if (!userID) continue;

      result[employee.biometricId] = {
        holidays: holidaySet,
        suspensions: suspensionSet,
        suspensionRangesByDate,
        leaveWithPay: leaveWithPaySet,
        leaveWithoutPay: leaveWithoutPaySet,
        ...(isArrayDate ? { leaveWithoutPaySetArray } : {}),
        mandatoryLeave: mandatoryLeaveSet,
        manualDtr: manualDtrSet,
        ob: obSet,
        timeoff: timeoffSet,
        schedule: employee?.selectedSchedule,
        workingDaysSet,
        workingDaysSetArray,
        workingDaysCount,
        isTwentyFourSeven,
        twentyFourSeven: employee?.twentyFourSeven,
        airportDates
      };
    }

    return result;
  };
